/**
 * SSL — Shape Shifters Level format, version 1.
 * Shared by runtime, editor, and (later) server-side validation.
 * See docs/ARCHITECTURE.md §4. Schema changes bump CURRENT_FORMAT_VERSION and
 * add a migration in src/format/migrations/.
 */

export const CURRENT_FORMAT_VERSION = 1;

/** Object types available in format v1 (grows additively; removals need a migration). */
export type ObjectType =
  | 'block'
  | 'spike'
  | 'endgate'
  | 'decoration'
  | 'portal'
  | 'pad'
  | 'orb'
  | 'slope' // props: dir 'up'|'down', grade 1|0.5
  | 'oneway' // land-through platform
  | 'checkpoint' // platformer auto-checkpoint
  | 'startpos' // editor playtest spawn override (props: form?, speed?)
  | 'trigger' // gameplay trigger (props.kind from TRIGGER_KINDS + per-kind fields)
  | 'collectible' // props: kind 'key'|'shard'|'mark'; keys: itemId (default 1)
  | 'lock' // solid until items[itemId] ≥ needed (props: itemId, needed)
  | 'text'; // neon world text (props: text, size?; decorative, no collision)

/**
 * Trigger kinds (props.kind for type='trigger'). Position-activated when the
 * player crosses the trigger's x (once per attempt), or spawn-activated when
 * props.spawnOnly is true. Per-kind props:
 * - move:    target (group), dx, dy (blocks), duration (s), easing
 * - rotate:  target, degrees, duration, easing        (render-side rotation)
 * - alpha:   target, alpha (0..1), duration
 * - toggle:  target, visible (bool) — hides render AND collision
 * - color:   channel, hex, duration
 * - pulse:   channel, hex, duration (flash out and back)
 * - spawn:   target (group whose triggers fire), delay (s)
 * - stop:    target (cancels that group's running tweens)
 * - scale:   target, scale (factor), duration, easing   (render-side scale)
 * - followy: target, duration, factor — group mirrors player Y movement
 * - pause:   target — suspends the group's running tweens
 * - resume:  target — resumes suspended tweens where they left off
 * - camera:zoom    zoom (factor), duration, easing
 * - camera:offset  dx, dy (blocks), duration, easing
 * - camera:shake   strength (units), duration
 * - camera:rotate  degrees (view roll), duration, easing
 * - camera:static  dx, dy (block coords) — lock the camera at a point
 * - camera:free    release a static camera back to player-follow
 * - timewarp:      factor (0.25..2), duration (s of sim time)
 * - shader:*       strength, duration, easing — full-screen post effects
 *   (chromatic, pixelate, grayscale, sepia, invert, hue [strength=degrees],
 *   bulge [±1; negative pinches], shockwave [one-shot wave], glitch)
 * All: spawnOnly (bool), maxActivations (int ≥1; 0/absent = unlimited for
 * spawn-fired triggers; positional triggers are always once-per-attempt).
 */
export const TRIGGER_KINDS = [
  'move',
  'rotate',
  'scale',
  'alpha',
  'toggle',
  'color',
  'pulse',
  'spawn',
  'stop',
  'pause',
  'resume',
  'followy',
  'camera:zoom',
  'camera:offset',
  'camera:shake',
  'camera:rotate',
  'camera:static',
  'camera:free',
  'timewarp',
  'shader:chromatic',
  'shader:pixelate',
  'shader:grayscale',
  'shader:sepia',
  'shader:invert',
  'shader:hue',
  'shader:bulge',
  'shader:shockwave',
  'shader:shockline', // one-shot vertical wave sweeping from the player
  'shader:glitch',
  'shader:radial', // radial blur from the player
  'shader:motion', // horizontal motion blur
  'shader:lens', // lens-circle vignette
  'shader:split', // mirrored split-screen
  // Area effects: render-side fields around the trigger's position with
  // radius falloff — props: radius (blocks) + payload (dx/dy/degrees/scale/
  // alpha/hex). Re-triggering the same trigger id replaces its field.
  'area:move',
  'area:rotate',
  'area:scale',
  'area:fade',
  'area:tint',
  // Enter effects: objects animate in from the screen edge — props: dx/dy/
  // degrees/scale + radius (transition width, blocks). enter:off clears.
  'enter:move',
  'enter:rotate',
  'enter:scale',
  'enter:fade',
  'enter:off',
  // Gradient overlay between two colors (hex, hex2, alpha, vertical).
  'gradient',
  'gradient:off',
  // Keyframe path: target group follows props.path "dx,dy,t;dx,dy,t;…"
  // (block offsets, seconds per segment), optional loop.
  'keyframe',
  // Camera bounds: props side ('left'|'right'|'top'|'bottom') + value
  // (blocks); camera:edge-off clears all bounds.
  'camera:edge',
  'camera:edge-off',
  // Ends the level on activation (custom endings).
  'end',
  // Item system: pickup adds `factor` to item `target`; item:spawn-if fires
  // spawn group `value` when items[target] ≥ factor.
  'pickup',
  'item:spawn-if',
] as const;
export type TriggerKind = (typeof TRIGGER_KINDS)[number];

/** Easing names shared by triggers and (later) keyframes. */
export const EASINGS = ['linear', 'in', 'out', 'inout'] as const;
export type EasingName = (typeof EASINGS)[number];

/** Color channel definition (level-wide palette slots referenced by objects). */
export interface ColorChannel {
  id: number; // 1..999
  hex: string; // #rrggbb
  opacity?: number; // 0..1, default 1
}

/** props.kind values for type='portal'. */
export const PORTAL_KINDS = [
  'form:runner',
  'form:glider',
  'form:roller',
  'form:hopper',
  'form:streak',
  'form:vaulter',
  'form:blinker',
  'form:pendul',
  'gravity:up',
  'gravity:down',
  'speed:half',
  'speed:one',
  'speed:two',
  'speed:three',
  'speed:four',
  'size:mini',
  'size:normal',
  'mirror:on',
  'mirror:off',
  'dual:on',
  'dual:off',
  'teleport', // props: dx, dy (block offsets applied to the player)
] as const;
export type PortalKind = (typeof PORTAL_KINDS)[number];

/** props.kind values for type='pad'. */
export const PAD_KINDS = ['strong', 'soft', 'mega', 'flip'] as const;
export type PadKind = (typeof PAD_KINDS)[number];

/** props.kind values for type='orb'. */
export const ORB_KINDS = [
  'strong',
  'soft',
  'mega',
  'flip',
  'flip-boost',
  'slam',
  'blink', // teleport to the opposite surface (spider-style)
  'dash', // hold to dash level; release to resume physics
  'gravdash', // dash that also flips gravity on activation
] as const;
export type OrbKind = (typeof ORB_KINDS)[number];

export interface LevelObject {
  /** Unique within the level; stable across edits. */
  id: number;
  type: ObjectType;
  /** Center position, world units. */
  x: number;
  y: number;
  /** Degrees, counter-clockwise. Default 0. */
  rot?: number;
  scaleX?: number;
  scaleY?: number;
  flipX?: boolean;
  flipY?: boolean;
  /** Fine draw order within a z-layer. Default 0. */
  z?: number;
  opacity?: number;
  /** Group memberships (1..9999) — targets for triggers. */
  groups?: number[];
  /** Color channel id; overrides the default palette for this object. */
  colorChannel?: number;
  /** Per-object HSV shift applied to its color: h in degrees, s/v deltas −1..1. */
  hsv?: { h: number; s: number; v: number };
  /** Per-type extra properties (validated per type). */
  props?: Record<string, number | string | boolean>;
  /** Editor-only metadata; stripped for runtime. */
  editor?: Record<string, unknown>;
}

export interface LevelMeta {
  name: string;
  author: string;
  description?: string;
  /** Monotonic revision counter, bumped on save. */
  revision: number;
  createdAt: string; // ISO-8601
  updatedAt: string; // ISO-8601
  /** Author-estimated difficulty 1–10 (community rating arrives in Phase 6). */
  difficulty?: number;
  tags?: string[];
}

export interface SongRef {
  /** Built-in track id (content/music) or, later, imported-audio id. */
  id: string;
  bpm: number;
  /** Playback offset applied at level start, milliseconds. */
  offsetMs: number;
}

export interface LevelSettings {
  /** 'normal' = auto-scroll rhythm mode; 'platformer' = free movement. */
  mode: 'normal' | 'platformer';
  speed: 'half' | 'one' | 'two' | 'three' | 'four';
  /** Player form at level start. Default 'runner'. */
  startForm?:
    'runner' | 'glider' | 'roller' | 'hopper' | 'streak' | 'vaulter' | 'blinker' | 'pendul';
  /** Neon accent palette for background/ground/blocks (hex colors). */
  accentColor: string;
  hazardColor: string;
  backgroundColor: string;
}

export interface LevelData {
  formatVersion: number;
  meta: LevelMeta;
  song: SongRef;
  settings: LevelSettings;
  /** Color channel palette (optional; objects reference channels by id). */
  colors?: ColorChannel[];
  objects: LevelObject[];
}
